import { FC, useMemo } from 'react'
import { Header as UiHeader, Help } from '@saasalerts/ui'
import useUser from '@/hooks/useUser'
import useAuthorization from '@/hooks/useAuthorization'
import { useRouter } from 'next/router'
import { useTheme } from '@/contexts/ThemeContext'
import {
  faMoon,
  faSun,
  faCircleQuestion,
} from '@awesome.me/kit-e270131d0a/icons/classic/regular'
import { Logo } from '@/components/Layout'
import { HELP_MENU_ITEMS } from '@/utils/helpMenuItems'
import { groupBy } from 'lodash'

export const Header: FC<React.PropsWithChildren<unknown>> = () => {
  const router = useRouter()
  const { logout, mutate } = useUser()
  const { data: authData } = useAuthorization()
  const { theme, toggleTheme } = useTheme()

  const userRole = useMemo(() => {
    if (authData && authData?.isMSPAdmin) return 'MSP Admin'
    if (authData && !authData.isMSPAdmin) return 'MSP User'
    return undefined
  }, [authData])

  const handleUserAction = async (action: string) => {
    if (action === 'logout') {
      await logout()
      mutate()
      return
    }
    if (action === 'settings') {
      router.push('/settings')
    }
  }

  const helpSections = useMemo(() => {
    const grouped = groupBy(HELP_MENU_ITEMS, 'group')

    return Object.entries(grouped).map(([group, items]) => ({
      id: group.toLowerCase(),
      title: group.toUpperCase(),
      items: items.map((item) => ({
        id: item.id,
        text: item.name,
        icon: item.icon,
        onClick: (e: React.MouseEvent) => {
          e.preventDefault()
          window.open(item.url, '_blank', 'noopener,noreferrer')
        },
      })),
    }))
  }, [])

  const helpFooter = useMemo(
    () => ({
      versionInfo: [
        {
          label: 'Version',
          value: process.env.APP_VERSION || 'dev',
        },
        { label: 'Mode', value: process.env.NODE_ENV || 'development' },
      ],
    }),
    []
  )

  return (
    <UiHeader className="print:!hidden">
      <UiHeader.Logo logoHeight="h-10">
        <Logo className="h-10" type="light" />
      </UiHeader.Logo>

      <UiHeader.Widget
        icon={theme === 'dark' ? faSun : faMoon}
        tooltip={
          theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'
        }
        onClick={toggleTheme}
      />

      <UiHeader.Widget icon={faCircleQuestion} tooltip="Help">
        <Help sections={helpSections} footer={helpFooter} />
      </UiHeader.Widget>

      <UiHeader.UserProfile
        userProfile={{
          name: authData?.name ?? 'User',
          title: userRole,
          email: authData?.email ?? '',
          avatar: '/images/logo.svg',
        }}
        onUserAction={handleUserAction}
      />
    </UiHeader>
  )
}
